import {
  Hand, Type, AlignLeft, Mail, Phone, Hash, Link2, CircleDot, CheckSquare, ChevronDown, ToggleLeft,
  CalendarDays, CalendarCheck, Video, Layers, Quote, Star, Gauge, ThumbsUp, Image as ImageIcon, ShieldCheck,
} from "lucide-react";

export const BLOCK_TYPES = [
  { type: "welcome", label: "Welcome screen", icon: Hand, group: "Screens" },
  { type: "statement", label: "Statement", icon: Quote, group: "Screens" },
  { type: "group", label: "Question group", icon: Layers, group: "Screens" },
  { type: "short_text", label: "Short text", icon: Type, group: "Text" },
  { type: "long_text", label: "Long text", icon: AlignLeft, group: "Text" },
  { type: "number", label: "Number", icon: Hash, group: "Text" },
  { type: "email", label: "Email", icon: Mail, group: "Contact" },
  { type: "phone", label: "Phone", icon: Phone, group: "Contact" },
  { type: "url", label: "Website", icon: Link2, group: "Contact" },
  { type: "single_choice", label: "Multiple choice", icon: CircleDot, group: "Choice" },
  { type: "multi_choice", label: "Checkboxes", icon: CheckSquare, group: "Choice" },
  { type: "picture_choice", label: "Picture choice", icon: ImageIcon, group: "Choice" },
  { type: "dropdown", label: "Dropdown", icon: ChevronDown, group: "Choice" },
  { type: "yes_no", label: "Yes / No", icon: ToggleLeft, group: "Choice" },
  { type: "rating", label: "Rating", icon: Star, group: "Rating" },
  { type: "opinion_scale", label: "Opinion scale", icon: Gauge, group: "Rating" },
  { type: "nps", label: "Net Promoter Score", icon: ThumbsUp, group: "Rating" },
  { type: "date", label: "Date", icon: CalendarDays, group: "Other" },
  { type: "consent", label: "Legal / consent", icon: ShieldCheck, group: "Other" },
  { type: "calendlo", label: "T&T Calendlo", icon: Video, group: "Booking" },
  { type: "calendar_meet", label: "Google Meet booking", icon: Video, group: "Booking" },
  { type: "calendly", label: "Calendly booking", icon: CalendarCheck, group: "Booking" },
];

export const typeMeta = (t) => BLOCK_TYPES.find((b) => b.type === t) || BLOCK_TYPES[3];
export const CHOICE_TYPES = ["single_choice", "multi_choice", "dropdown", "picture_choice"];
export const SCREEN_TYPES = ["welcome", "statement"];
export const TEXT_TYPES = ["short_text", "long_text", "email", "phone", "url", "number"];
export const SCALE_TYPES = ["rating", "opinion_scale", "nps"];
export const FONTS = ["Geist", "Space Grotesk", "DM Sans", "Manrope", "IBM Plex Sans", "Playfair Display", "Fraunces", "JetBrains Mono"];

export const DEFAULT_LABELS = {
  next: "OK", submit: "Submit", enter_hint: "press Enter ↵", type_placeholder: "Type your answer here…",
  select_placeholder: "Select an option", other: "Other", other_placeholder: "Type your answer",
  choose_many: "Choose as many as you like", required: "Please fill this in",
  email_invalid: "Hmm… that email doesn't look right", phone_invalid: "Please enter a valid phone number",
  consent_accept: "I accept", consent_decline: "I don't accept", slots_loading: "Finding open times…",
  slots_empty: "No open times in the next few days. We'll reach out to schedule.", timezone_note: "Times shown in IST",
};

export const DEFAULT_THEME = {
  logo_url: "/tt-logo.webp", logo_position: "left", logo_height: 28, bg_color: "#000000", bg_image_url: "",
  bg_overlay: 0.5, text_color: "#ffffff", font: "Geist", button_radius: "md", align: "left",
};

export const uid = (p = "b") => `${p}_${Math.random().toString(36).slice(2, 8)}`;

export function newBlock(type) {
  const base = { id: uid(), type, label: "", description: "", required: false };
  if (CHOICE_TYPES.includes(type)) base.options = ["Option A", "Option B"];
  if (type === "group") base.children = [];
  if (type === "welcome") Object.assign(base, { label: "Welcome", button_label: "Start" });
  if (type === "statement") base.button_label = "Continue";
  if (type === "rating") base.scale_max = 5;
  if (type === "opinion_scale") Object.assign(base, { scale_min: 1, scale_max: 10, scale_label_left: "Not likely", scale_label_right: "Very likely" });
  if (type === "nps") Object.assign(base, { scale_label_left: "Not at all likely", scale_label_right: "Extremely likely" });
  if (type === "consent") Object.assign(base, { label: "Terms", description: "I agree to be contacted about my enquiry.", required: true });
  if (type === "calendly") Object.assign(base, { label: "Pick a time that suits you", required: true, calendly_url: "" });
  if (type === "calendar_meet") Object.assign(base, {
    label: "Pick a time for a Google Meet", required: true, calendar_emails: [],
    meeting_duration_min: 30, meeting_window_days: 7, meeting_slot_start: "12:00",
    meeting_slot_end: "17:00", meeting_days: [0, 1, 2, 3, 4, 5], min_notice_hours: 2,
    meeting_title: "T&T Meet I", meeting_description: "",
  });
  if (type === "calendlo") Object.assign(base, {
    label: "Book your call", required: true, calendar_emails: [], display_mode: "overview",
    event_name: "Discovery call", event_description: "A quick call to understand your event and how we can help.",
    meeting_duration_min: 30, slot_interval_min: 30, buffer_min: 0, max_per_day: 0, meeting_window_days: 14,
    meeting_slot_start: "11:00", meeting_slot_end: "19:00", meeting_days: [0, 1, 2, 3, 4, 5], min_notice_hours: 2,
    reminders: [1440, 60],
  });
  return base;
}

export const newEnding = () => ({ id: uid("end"), title: "Thanks!", body: "", outcome: "neutral", show_meet: true, transition: "fade" });

export const endingsOf = (form) => {
  const s = form?.settings || {};
  if (s.endings?.length) return s.endings;
  const ty = s.thank_you || {};
  return [{ id: "end_default", outcome: "neutral", show_meet: true, ...ty, title: ty.title || "Thanks — you're in." }];
};

const mapTree = (blocks, fn) => blocks.flatMap((b) => {
  const r = fn(b);
  if (r === null) return [];
  if (r.children) return [{ ...r, children: mapTree(r.children, fn) }];
  return [r];
});

export const findBlock = (blocks, id) => {
  for (const b of blocks) {
    if (b.id === id) return b;
    const c = (b.children || []).find((x) => x.id === id);
    if (c) return c;
  }
  return null;
};

export const updateBlock = (blocks, id, patch) => mapTree(blocks, (b) => (b.id === id ? { ...b, ...patch } : b));
export const removeBlock = (blocks, id) => mapTree(blocks, (b) => (b.id === id ? null : b));

const swap = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const a = [...arr];
  [a[i], a[j]] = [a[j], a[i]];
  return a;
};

export function moveBlock(blocks, id, dir) {
  const i = blocks.findIndex((b) => b.id === id);
  if (i >= 0) return swap(blocks, i, dir);
  return blocks.map((b) => {
    const ci = (b.children || []).findIndex((c) => c.id === id);
    return ci >= 0 ? { ...b, children: swap(b.children, ci, dir) } : b;
  });
}

export const addChild = (blocks, groupId, child) =>
  blocks.map((b) => (b.id === groupId ? { ...b, children: [...(b.children || []), child] } : b));

export const duplicateBlock = (blocks, id) => {
  const src = findBlock(blocks, id);
  const copy = { ...src, id: uid(), logic: null, children: src.children?.map((c) => ({ ...c, id: uid() })) };
  const i = blocks.findIndex((b) => b.id === id);
  if (i >= 0) return [...blocks.slice(0, i + 1), copy, ...blocks.slice(i + 1)];
  return blocks.map((b) => {
    const ci = (b.children || []).findIndex((c) => c.id === id);
    if (ci < 0) return b;
    return { ...b, children: [...b.children.slice(0, ci + 1), copy, ...b.children.slice(ci + 1)] };
  });
};

export const flatQuestions = (blocks) =>
  blocks.flatMap((b) => (b.type === "group" ? b.children || [] : [b])).filter((b) => !SCREEN_TYPES.includes(b.type));

export const topIndexOf = (blocks, id) => blocks.findIndex((b) => b.id === id || (b.children || []).some((c) => c.id === id));

export const optionsFor = (b) => (b.type === "yes_no" ? ["Yes", "No"] : (b.options || []).filter(Boolean));

export function computeScore(blocks, answers) {
  return flatQuestions(blocks).reduce((t, b) => {
    const sc = b.option_scores || {};
    const v = answers[b.id];
    return t + (Array.isArray(v) ? v : [v]).reduce((a, x) => a + (Number(sc[x]) || 0), 0);
  }, 0);
}

const num = (v) => Number(String(Array.isArray(v) ? v[0] : v ?? "").replace(/[,₹\s]/g, "")) || 0;

function calc(formula, answers, vars) {
  const toks = String(formula || "0").replace(/×/g, "*").match(/\d+(\.\d+)?|[A-Za-z_][\w-]*|[-+*/%()]/g) || [];
  let i = 0;
  const peek = () => toks[i];
  const atom = () => {
    const t = toks[i++];
    if (t === "(") { const v = expr(); i++; return v; }
    if (t === "-") return -atom();
    if (/^\d/.test(t || "")) return Number(t);
    if (t === undefined) return 0;
    return t in vars ? vars[t] : num(answers[t]);
  };
  const term = () => { let v = atom(); while (["*", "/", "%"].includes(peek())) { const o = toks[i++]; const r = atom(); v = o === "*" ? v * r : o === "/" ? (r ? v / r : 0) : v % r; } return v; };
  const expr = () => { let v = term(); while (["+", "-"].includes(peek())) { const o = toks[i++]; const r = term(); v = o === "+" ? v + r : v - r; } return v; };
  try { return expr(); } catch { return 0; }
}

const IST_FMT = (iso, o) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", ...o });

export const MEETING_VARS = ["meeting_datetime", "meeting_date", "meeting_time", "meeting_day"];

export function meetingVarsFor(iso) {
  if (!iso || isNaN(new Date(iso))) return {};
  const v = {
    meeting_date: IST_FMT(iso, { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
    meeting_time: `${IST_FMT(iso, { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase()} IST`,
    meeting_day: IST_FMT(iso, { weekday: "long" }),
  };
  v.meeting_datetime = `${v.meeting_date} at ${v.meeting_time}`;
  return v;
}

function meetingVars(form, answers) {
  const out = {};
  (form?.blocks || []).flatMap((b) => (b.type === "group" ? b.children || [] : [b]))
    .filter((b) => ["calendlo", "calendar_meet"].includes(b.type)).forEach((b) => {
      const raw = answers?.[b.id];
      const iso = typeof raw === "string" ? raw : raw?.start;
      const v = meetingVarsFor(iso);
      if (!v.meeting_datetime) return;
      Object.entries(v).forEach(([k, x]) => { out[k] = x; out[`${b.id}_${k.slice(8)}`] = x; });
      out[b.id] = v.meeting_datetime;
    });
  return out;
}

export function computeVars(form, answers) {
  const out = meetingVars(form, answers);
  (form?.settings?.variables || []).forEach((v) => { if (v.id) out[v.id] = calc(v.formula, answers, out); });
  return out;
}

export function evalCond(c, answers, score, vars = {}) {
  const v = c.ref === "__score" ? score : c.ref?.startsWith("var:") ? vars[c.ref.slice(4)] : answers[c.ref];
  const arr = Array.isArray(v) ? v : v === undefined || v === null || v === "" ? [] : [v];
  const s = (x) => String(x).trim().toLowerCase();
  switch (c.op) {
    case "is": return arr.some((x) => s(x) === s(c.value));
    case "is_not": return !arr.some((x) => s(x) === s(c.value));
    case "contains": return arr.some((x) => s(x).includes(s(c.value || "")));
    case "gt": return num(v) > num(c.value);
    case "gte": return num(v) >= num(c.value);
    case "lt": return num(v) < num(c.value);
    case "lte": return num(v) <= num(c.value);
    case "answered": return arr.length > 0;
    case "not_answered": return arr.length === 0;
    default: return false;
  }
}

export function logicTarget(step, answers, score, vars = {}) {
  const lg = step?.logic;
  if (!lg) return null;
  for (const r of lg.rules || []) {
    const res = (r.conditions || []).map((c) => evalCond(c, answers, score, vars));
    if (r.to && res.length && (r.match === "any" ? res.some(Boolean) : res.every(Boolean))) return r.to;
  }
  return lg.otherwise || null;
}

export function recall(text, answers) {
  if (!text) return text;
  return text.replace(/\{\{\s*([\w-]+)\s*\}\}/g, (_, id) => {
    const v = answers?.[id];
    return v === undefined || v === null ? "" : Array.isArray(v) ? v.join(", ") : String(v);
  }).replace(/\s+([,.!?])/g, "$1");
}

export const assetUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export const publicUrl = (slug) => `${process.env.REACT_APP_FORMS_BASE_URL || window.location.origin + "/f"}/${slug}`;
